import React, { useState, useMemo } from "react";
import "../css/QuizResults.css";
import api from "../services/api";

// Utility for category breakdown
function getCategoryStats(data) {
  const stats = {};
  data.forEach(q => {
    const cat = q.category || "Other";
    if (!stats[cat]) stats[cat] = { total: 0, correct: 0 };
    stats[cat].total++;
    if (String(q.userAnswer).trim() === String(q.correctChoice).trim()) stats[cat].correct++;
  });
  return stats;
}

// Export helpers
function exportToJSON(data, filename = "quiz_results.json") {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}
function exportToCSV(data, filename = "quiz_results.csv") {
  if (!data?.length) return;
  const headers = Object.keys(data[0]);
  const rows = data.map(row => headers.map(h => `"${String(row[h] ?? "")}"`).join(","));
  const csv = [headers.join(","), ...rows].join("\r\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

export default function QuizResultsSummaryModal({
  isOpen,
  onClose,
  summaryData = [],
  onRetake,
  userRole = "standard"
}) {
  const [filter, setFilter] = useState("all"); // all | missed | lowconf

  // Prepare filtered data
  const filtered = useMemo(() => {
    if (filter === "all") return summaryData;
    if (filter === "missed")
      return summaryData.filter(q => String(q.userAnswer).trim() !== String(q.correctChoice).trim());
    if (filter === "lowconf")
      return summaryData.filter(q => q.confidence && q.confidence <= 1);
    return summaryData;
  }, [filter, summaryData]);

  // Flag/Report question functionality
  const handleFlagQuestion = async (question, questionIndex) => {
    const reason = prompt(
      `Report an issue with this question:\n\n"${question.question}"\n\nSelect a reason:\n\n1. Incorrect Answer\n2. Spelling/Grammar Error\n3. Question Unclear\n4. Missing Choice E\n5. Other\n\nEnter 1-5:`
    );
    
    if (!reason) return; // User cancelled
    
    const reasonMap = {
      '1': 'incorrect_answer',
      '2': 'typo', 
      '3': 'unclear',
      '4': 'missing_choice',
      '5': 'other'
    };
    
    const reportReason = reasonMap[reason];
    if (!reportReason) {
      alert('Invalid selection. Please try again.');
      return;
    }
    
    const comment = prompt('Additional comments (optional):') || '';
    
    try {
      await api.post('/reports', {
        question_id: question.question_id || `unknown_${questionIndex}`,
        report_reason: reportReason,
        report_comment: comment
      });
      
      alert('Question flagged for review. Thank you for helping us improve!');
    } catch (error) {
      console.error('Failed to flag question:', error);
      alert('Failed to flag question. Please try again later.');
    }
  };

  // Copy Q/A pairs to clipboard (non-proprietary data only)
  function handleCopy() {
    const toCopy = filtered.map((q, index) =>
      `Question ${index + 1}: ${String(q.userAnswer).trim() === String(q.correctChoice).trim() ? "Correct ✔️" : "Incorrect ✘"}\nYour Answer: ${q.userAnswer}\nCategory: ${q.category || "General"}\nNote: ${q.note || "None"}\n`
    ).join("\n---\n");
    navigator.clipboard.writeText(toCopy);
    alert("Results copied to clipboard! (Question content excluded for IP protection)");
  }

  // Safe export function that removes proprietary content
  function createSafeExportData(data) {
    return data.map((q, index) => ({
      questionNumber: index + 1,
      questionId: q.question_id || `unknown_${index}`,
      // Proprietary content removed for IP protection
      userAnswerLetter: q.userAnswer,
      isCorrect: String(q.userAnswer).trim() === String(q.correctChoice).trim(),
      category: q.category || "General",
      confidence: q.confidence && q.confidence !== 1 ? q.confidence : null,
      note: q.note || "",
      timeSpent: q.timeSpent || 0
    }));
  }

  // Print safe version without proprietary content
  function handlePrint() {
    // Create a safe print version without question content
    const safeData = createSafeExportData(filtered);
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Quiz Results - ${new Date().toLocaleDateString()}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            h1 { color: #333; border-bottom: 2px solid #333; }
            .result-item { margin: 15px 0; padding: 10px; border: 1px solid #ddd; }
            .correct { background-color: #d4edda; }
            .incorrect { background-color: #f8d7da; }
          </style>
        </head>
        <body>
          <h1>Quiz Results Summary</h1>
          <p><strong>Date:</strong> ${new Date().toLocaleDateString()}</p>
          <p><strong>Questions:</strong> ${safeData.length}</p>
          <p><strong>IP Protection:</strong> Question content excluded</p>
          ${safeData.map(item => `
            <div class="result-item ${item.isCorrect ? 'correct' : 'incorrect'}">
              <strong>Question ${item.questionNumber}:</strong> ${item.isCorrect ? 'Correct ✓' : 'Incorrect ✗'}<br>
              <strong>Your Answer:</strong> ${item.userAnswerLetter}<br>
              <strong>Category:</strong> ${item.category}<br>
              ${item.confidence ? `<strong>Confidence:</strong> ${item.confidence}/5<br>` : ''}
              ${item.note ? `<strong>Note:</strong> ${item.note}<br>` : ''}
            </div>
          `).join('')}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
    printWindow.close();
  }

  // Export notes only
  function handleExportNotes() {
    const notes = summaryData.filter(q => q.note).map((q, index) => ({
      questionNumber: index + 1,
      questionId: q.question_id || `unknown_${index}`,
      // Proprietary question content removed for IP protection
      note: q.note
    }));
    exportToJSON(notes, "quiz_notes.json");
  }

  // Category stats
  const categoryStats = useMemo(() => getCategoryStats(summaryData), [summaryData]);

  if (!isOpen) return null;

  return (
    <div className="modal-backdrop">
      <div className="quiz-results-modal">
        <div className="quiz-results-header">
          <h3>Quiz Results Summary</h3>
          <button 
            className="modal-close"
            onClick={onClose}
            aria-label="Close results"
          >
            ✕
          </button>
        </div>

        {/* --- Category Breakdown --- */}
        <div className="category-breakdown">
          <strong>Category Breakdown:</strong>
          <ul className="category-stats">
            {Object.entries(categoryStats).map(([cat, stat]) => (
              <li key={cat} className="category-stat-item">
                <strong>{cat}:</strong> {stat.correct}/{stat.total}
                <span className="category-percentage">
                  ({Math.round((stat.correct / stat.total) * 100)}%)
                </span>
              </li>
            ))}
          </ul>
        </div>

        {/* --- Action Bar --- */}
        <div className="quiz-results-actions">
          <div className="filter-section">
            <label className="filter-label">Show:</label>
            <div className="filter-buttons">
              <button 
                onClick={() => setFilter("all")} 
                className={`filter-btn ${filter === "all" ? "active" : ""}`}
              >
                All
              </button>
              <button 
                onClick={() => setFilter("missed")} 
                className={`filter-btn ${filter === "missed" ? "active" : ""}`}
              >
                Missed
              </button>
              <button 
                onClick={() => setFilter("lowconf")} 
                className={`filter-btn ${filter === "lowconf" ? "active" : ""}`}
              >
                Low Confidence
              </button>
            </div>
          </div>
          
          <div className="action-buttons">
            <button className="action-btn primary" onClick={onRetake}>Retake These</button>
            <button className="action-btn" onClick={handleExportNotes}>Export Notes</button>
            <button className="action-btn" onClick={handleCopy}>Copy</button>
            <button className="action-btn" onClick={() => exportToJSON(createSafeExportData(filtered), "quiz_results_safe.json")}>Export JSON</button>
            <button className="action-btn" onClick={() => exportToCSV(createSafeExportData(filtered), "quiz_results_safe.csv")}>Export CSV</button>
            <button className="action-btn" onClick={handlePrint}>Print</button>
          </div>
        </div>

        {/* --- Question List --- */}
        <div className="quiz-results-questions">
          {filtered.length === 0 && (
            <div className="no-questions">No questions match the current filter.</div>
          )}
          {filtered.map((q, i) => {
            const isCorrect = String(q.userAnswer).trim() === String(q.correctChoice).trim();
            return (
              <div key={i} className={`question-result-card ${isCorrect ? 'correct' : 'incorrect'}`}>
                {/* Question Header */}
                <div className="question-result-header">
                  <div className="question-number">Q{i + 1}</div>
                  <div className="question-status-icon">
                    {isCorrect ? (
                      <span className="status-correct">✓</span>
                    ) : (
                      <span className="status-incorrect">✗</span>
                    )}
                  </div>
                </div>

                {/* Question Text */}
                <div className="question-result-text">
                  <strong>{q.question}</strong>
                </div>

                {/* Media if present */}
                {q.mediaUrl && (
                  <div className="question-media">
                    {q.mediaUrl.match(/\.(jpg|jpeg|png|gif)$/i) && (
                      <img 
                        src={q.mediaUrl} 
                        alt="Question media" 
                        className="media-image" 
                      />
                    )}
                    {q.mediaUrl.match(/\.(mp3|wav|ogg)$/i) && (
                      <audio controls src={q.mediaUrl} className="media-audio" />
                    )}
                    {q.mediaUrl.match(/\.(mp4|webm|mov)$/i) && (
                      <video controls src={q.mediaUrl} className="media-video" />
                    )}
                  </div>
                )}

                {/* Answer Options - Show all choices with highlighting */}
                <div className="answer-choices">
                  <div className="choice-section">
                    <div className="choice-label">All Answer Choices:</div>
                    <div className="choices-list">
                      {['A', 'B', 'C', 'D', 'E'].map(choice => {
                        const choiceText = q[`choice_${choice.toLowerCase()}`];
                        if (!choiceText) return null;
                        
                        const isUserAnswer = q.userAnswer === choice;
                        const isCorrectAnswer = q.correctChoice === choice;
                        
                        return (
                          <div 
                            key={choice}
                            className={`choice-item ${isUserAnswer ? 'user-choice' : ''} ${isCorrectAnswer ? 'correct-choice' : ''}`}
                          >
                            <span className="choice-letter">{choice}.</span>
                            <span className="choice-text">{choiceText}</span>
                            {isUserAnswer && <span className="choice-indicator">Your Answer</span>}
                            {isCorrectAnswer && <span className="choice-indicator correct">Correct Answer</span>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Answer Summary */}
                <div className="answer-summary">
                  <div className="answer-line">
                    <strong>Your Answer:</strong> 
                    <span className={`answer-text ${isCorrect ? 'correct' : 'incorrect'}`}>
                      {q.userAnswer}
                    </span>
                  </div>
                  {!isCorrect && (
                    <div className="answer-line">
                      <strong>Correct Answer:</strong> 
                      <span className="answer-text correct">{q.correctChoice}</span>
                    </div>
                  )}
                </div>

                {/* Metadata */}
                <div className="question-metadata">
                  {q.confidence && (
                    <div className="confidence-display">
                      <strong>Confidence:</strong>
                      <span className={`confidence-level ${
                        q.confidence >= 3 ? 'high' : 
                        q.confidence === 2 ? 'medium' : 'low'
                      }`}>
                        {q.confidence >= 3 ? "High" : q.confidence === 2 ? "Medium" : "Low"}
                      </span>
                    </div>
                  )}
                  {q.category && (
                    <div className="category-display">
                      <strong>Category:</strong> <span>{q.category}</span>
                    </div>
                  )}
                  <div className="question-actions">
                    <button 
                      className="flag-question-btn"
                      onClick={() => handleFlagQuestion(q, i)}
                      title="Report issue with this question"
                    >
                      🚩 Flag Question
                    </button>
                  </div>
                </div>

                {/* Rationale */}
                {q.rationale && (
                  <div className="rationale-section">
                    <strong>Explanation:</strong>
                    <div className="rationale-text">{q.rationale}</div>
                  </div>
                )}

                {/* Note */}
                {q.note && (
                  <div className="note-section">
                    <strong>📝 Your Note:</strong>
                    <div className="note-text">{q.note}</div>
                  </div>
                )}

                {/* Admin actions */}
                {userRole === "admin" && (
                  <div className="admin-actions">
                    <button className="admin-btn" disabled>Edit Question</button>
                    <button className="admin-btn" disabled>Mark Ambiguous</button>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Close Button */}
        <div className="quiz-results-footer">
          <button className="close-btn" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
